import { call, put, select, takeLatest } from "redux-saga/effects";
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { createTicket } from "../../../../features/Chat/api/agent/createTicket";
import { createSendbirdUser } from "../../../../features/Chat/api/agent/createSendbirdUser";
const { REACT_APP_CONTEXT_PATH } = process.env;

// Initial state
const initialState = {
  arsAgentId: null,
  accessToken: null,
  startTime: null,
  channelUrl: null,
  ticketId: null,
  loading: false,
  error: false,
  loaded: false,
  step: null,
  status: null,
  message: null,
  errorType: null,
  unavailableCount: 0,
};

// Slice
const connectChatServerSlice = createSlice({
  name: "connectChatServer",
  initialState,
  reducers: {
    initChatStatus(state) {
      return {
        ...state,
        arsAgentId: null,
        accessToken: null,
        startTime: null,
        channelUrl: null,
        ticketId: null,
        loading: false,
        error: false,
        loaded: false,
        step: null,
        status: null,
        message: null,
        errorType: null,
      };
    },
    connectChatServerStart(state) {
      return { ...state, loading: true, error: false, loaded: false, step: 0 };
    },
    fetchDataSuccess(state, action) {
      return {
        ...state,
        loading: false,
        data: action.payload,
        error: false,
        loaded: true,
      };
    },
    fetchDataFailure(state, action) {
      return {
        ...state,
        loading: false,
        data: action.payload,
        error: action.payload,
        loaded: true,
      };
    },
    setArsAgentId(state, action) {
      const { arsAgentId } = action.payload;
      return {
        ...state,
        data: action.payload,
        arsAgentId,
        step: 1,
      };
    },
    makeSendbirdUser(state, action) {
      const { accessToken } = action.payload;
      return {
        ...state,
        startTime: new Date().getTime(),
        accessToken,
        step: 2,
      };
    },
    setChannelUrl(state, action) {
      const { channelUrl, ticketId } = action.payload;
      return {
        ...state,
        channelUrl,
        ticketId,
        step: 3,
      };
    },
    setStatus(state, action) {
      const { status, errorType, unavailableCount } = action.payload;
      return {
        ...state,
        status,
        loading: false,
        errorType,
        unavailableCount,
      };
    },
    setMessage(state, action) {
      const { message } = action.payload;
      return {
        ...state,
        message,
      };
    },
    initUnavailableCount(state) {
      return {
        ...state,
        unavailableCount: 0,
        status: null,
        errorType: null,
        message: null
      };
    },
  },
});

export const {
  initChatStatus,
  connectChatServerStart,
  fetchDataSuccess,
  fetchDataFailure,
  setArsAgentId,
  makeSendbirdUser,
  setChannelUrl,
  setStatus,
  setMessage,
  initUnavailableCount,
} = connectChatServerSlice.actions;

export default connectChatServerSlice.reducer;

function* fetchDataSaga(action) {
  const tempUserId = yield select((state) => state.parseUrl.userId);
  const userId = tempUserId??"".replaceAll(" ", "+");

  const channelId = yield select((state) => state.parseUrl.channelId);
  const { headers = {}, apiUrl, data = {} } = action.payload;
  const peerIp = yield select((state) => state.peerIp.ip);

  const unavailableCount = yield select((state) => state.connectChatServer.unavailableCount);
  const unavailableMsg = '{\n  "type" : "BOT",\n  "content" : [ {\n    "type" : "card",\n    "value" : [ {\n      "type" : "text",\n      "value" : "지금은 채팅 상담 연결이 어렵습니다. \n 잠시 후 다시 시도해주세요."\n    } ]\n  } ]\n}';
  
  const apiService = axios.create({
    headers: {
      ...headers,
      user_ip: peerIp
    },
    timeout: 10000,
    baseURL: REACT_APP_CONTEXT_PATH,
  });

  try {
    const response = yield call(apiService.post, apiUrl, data); //음성 삼당 정보 가져오기
    const { data: agentId } = response;

    if (agentId?.code === "0000") {
      yield put(fetchDataSuccess(agentId.data));
      if (agentId?.data) {
        const { id } = agentId.data;
  
        yield put(setArsAgentId({ arsAgentId: id }));
        //as is 는 음성이관 했는지 프론트에서 체크하려고 할 때 arsAgentId가 필요하였음
        // data?.transfer?.toAssignment?.agent?.id === arsAgentId로 비교하였음
        // 상담원 채팅 초기에 agentId를 세팅하고, 음성이관 데이터 받았을 때 매칭할 agentid를 비교해서 status를 세팅하고 음성이관을 클릭했을 때
        //status에 따라 전화 연결을 시켜주던 service-unavailable 페이지로 넘겨주면 될듯함
      } else {
        yield put(setMessage({ message: unavailableMsg }));
        yield put(setStatus({ status: "unavailable", errorType: "장애페이지 01-02", unavailableCount: unavailableCount + 1 }));
        return;
      }
    } else if (agentId?.code === "0307") {
      const { data: msg } = agentId;
      yield put(setMessage({ message: msg }));
      yield put(setStatus({ status: "unavailable:manual-operation" }));
      return;
    } else {
      yield put(setMessage({ message: unavailableMsg }));
      yield put(setStatus({ status: "unavailable", errorType: "장애페이지 01-02", unavailableCount: unavailableCount + 1 }));
      return;
    }
  } catch (error) {
    yield put(fetchDataFailure(error));
    yield put(setMessage({ message: unavailableMsg }));
    yield put(setStatus({ status: "unavailable", errorType: "장애페이지 01-01", unavailableCount: unavailableCount + 1 }));
    return;
  }

  try {
    const response = yield call(
      //sendbird user 생성
      //센드버드 사용자 생성
      apiService.post,
      createSendbirdUser.apiUrl,
      {
        user_id: userId,
        nickname: "고객",
      }
    );
    const { data: sendbirdUser } = response;

    if (sendbirdUser?.code === "0000") {
      yield put(fetchDataSuccess(sendbirdUser.data));

      if (sendbirdUser?.data?.access_token) {
        const data = sendbirdUser.data;
        const { access_token: accessToken } = data;
  
        yield put(makeSendbirdUser({ accessToken }));
      } else {
        yield put(setMessage({ message: unavailableMsg }));
        yield put(setStatus({ status: "unavailable", errorType: "장애페이지 02-02", unavailableCount: unavailableCount + 1 }));
        return;
      }
    } else if (sendbirdUser?.code === "0307") {
      const { data: msg } = sendbirdUser;
      yield put(setMessage({ message: msg }));
      yield put(setStatus({ status: "unavailable:manual-operation" }));
      return;
    } else {
      yield put(setMessage({ message: unavailableMsg }));
      yield put(setStatus({ status: "unavailable", errorType: "장애페이지 02-02", unavailableCount: unavailableCount + 1 }));
      return;
    }
  } catch (error) {
    yield put(fetchDataFailure(error));

    yield put(setMessage({ message: unavailableMsg }));
    yield put(setStatus({ status: "unavailable", errorType: "장애페이지 02-01", unavailableCount: unavailableCount + 1 }));
    return;
  }

  try {
    const channelCd = yield select((state) => state.checkArsStatus.channelCd);
    const channelId = yield select((state) => state.checkArsStatus.channelId);
    const tempUserId = yield select((state) => state.checkArsStatus.userId);
    const userId = tempUserId??"".replaceAll(" ", "+");

    const ticket = yield call(apiService.post, createTicket.apiUrl, {
      //티켓 생성 요청, 서버에서 티켓 받아오기
      inflowType: channelCd,
      channelId: channelId,
      userId: userId,
    });
    // const ticket = {
    //   data: {
    //     code: "3404",
    //     msg: '{\n  "type" : "BOT",\n  "content" : [ {\n    "type" : "card",\n    "value" : [ {\n      "type" : "text",\n      "value" : "12시부터 13시까지 점심시간으로 실시간 상담이 어려운 점 양해 부탁드립니다.\\r\\r\\r13시 이후 문의주시거나 아래를 통해 접수해 주시면 담당자 확인 후 순차적으로 답변드리겠습니다.\\r\\r\\r식사시간: \\r\\r12:00~13:00"\n    }, {\n      "type" : "buttons",\n      "value" : [ {\n        "action" : "webLink",\n        "label" : "고객문의 접수하기",\n        "value" : "https://emartapp.emart.com/customer/customerMain.do",\n        "data" : null,\n        "sessionId" : null,\n        "badge" : null\n      } ]\n    } ]\n  } ]\n}',
    //   },
    // };
    yield put(fetchDataSuccess(ticket.data));
    if (ticket?.data?.code === "0000") {
      const data = ticket.data.data;
      const { channelUrl, id: ticketId } = data;

      yield put(setChannelUrl({ channelUrl, ticketId }));
    } else if (ticket?.data?.code === "1404") {
      // 상담시간 아닐 때
      const { msg } = ticket?.data;

      yield put(setMessage({ message: msg }));
      yield put(setStatus({ status: "unavailable:service-hour", unavailableCount }));
    } else if (ticket?.data?.code === "3404") {
      // 상담원 없을 때
      const { msg } = ticket?.data;

      yield put(setMessage({ message: msg }));
      yield put(setStatus({ status: "unavailable:online", unavailableCount }));
    } else if (ticket?.data?.code === "0307") {
      const { data: msg } = ticket?.data;
      yield put(setMessage({ message: msg }));
      yield put(setStatus({ status: "unavailable:manual-operation" }));
      return;
    } else {
      yield put(setMessage({ message: unavailableMsg }));
      yield put(setStatus({ status: "unavailable", errorType: `장애페이지 03-02-${ticket?.data?.code}`, unavailableCount: unavailableCount + 1 }));
      return;
    }
  } catch (error) {
    yield put(fetchDataFailure(error));
    yield put(setMessage({ message: unavailableMsg }));
    yield put(setStatus({ status: "unavailable", errorType: "장애페이지 03-01", unavailableCount: unavailableCount + 1 }));
    return;
  }

  const channelUrl = yield select(
    (state) => state.connectChatServer.channelUrl
  );
  const accessToken = yield select(
    (state) => state.connectChatServer.accessToken
  );

  if (channelUrl != null && accessToken != null) {
    yield put(setStatus({ status: "available", unavailableCount }));
  }
}

// API 호출을 감시하는 Saga 함수
export function* connectChatServerSaga() {
  yield takeLatest(connectChatServerStart.type, fetchDataSaga);
}
